import { useState, useEffect, useMemo } from 'react';
import '../styles/AdminPedidos.css';

const ESTADOS = [
  { valor: 'confirmado',  etiqueta: 'Confirmado',  color: '#3498db' },
  { valor: 'en_proceso',  etiqueta: 'En proceso',  color: '#f39c12' },
  { valor: 'enviado',     etiqueta: 'Enviado',      color: '#9b59b6' },
  { valor: 'entregado',   etiqueta: 'Entregado',    color: '#27ae60' },
  { valor: 'cancelado',   etiqueta: 'Cancelado',    color: '#e74c3c' },
];

export default function AdminPedidos({ isOpen, onClose }) {
  const [pedidos, setPedidos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [expandido, setExpandido] = useState(null);
  const [actualizando, setActualizando] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setCargando(true);
    fetch('/api/pedidos')
      .then(r => r.json())
      .then(data => setPedidos(Array.isArray(data) ? data : []))
      .catch(e => console.error('Error cargando pedidos:', e))
      .finally(() => setCargando(false));
  }, [isOpen]);

  const pedidosFiltrados = useMemo(() => {
    return pedidos.filter(p => {
      if (filtroEstado && p.estado !== filtroEstado) return false;
      if (busqueda) {
        const q = busqueda.toLowerCase();
        return (
          p.id_pedido?.toLowerCase().includes(q) ||
          p.cliente?.nombre?.toLowerCase().includes(q) ||
          p.cliente?.email?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [pedidos, filtroEstado, busqueda]);

  const cambiarEstado = async (id_pedido, nuevoEstado) => {
    setActualizando(id_pedido);
    try {
      const res = await fetch(`/api/pedidos/${id_pedido}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      if (res.ok) {
        setPedidos(prev =>
          prev.map(p => p.id_pedido === id_pedido ? { ...p, estado: nuevoEstado } : p)
        );
      }
    } catch (e) {
      console.error('Error actualizando estado:', e);
    } finally {
      setActualizando(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="admin-pedidos-overlay"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="admin-pedidos-modal">

        {/* Header */}
        <div className="admin-pedidos-header">
          <h2>Panel de Pedidos</h2>
          <button className="btn-cerrar-panel" onClick={onClose} title="Cerrar">✕</button>
        </div>

        {/* Filtros */}
        <div className="admin-pedidos-filtros">
          <input
            type="text"
            className="panel-input"
            placeholder="🔍 ID, nombre o email..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
          <select
            className="panel-select"
            value={filtroEstado}
            onChange={e => setFiltroEstado(e.target.value)}
          >
            <option value="">Todos los estados</option>
            {ESTADOS.map(est => (
              <option key={est.valor} value={est.valor}>{est.etiqueta}</option>
            ))}
          </select>
          <span className="panel-contador">{pedidosFiltrados.length} pedido(s)</span>
        </div>

        {/* Lista de pedidos */}
        <div className="admin-pedidos-lista">
          {cargando ? (
            <p className="panel-mensaje">Cargando pedidos...</p>
          ) : pedidosFiltrados.length === 0 ? (
            <p className="panel-mensaje">No se encontraron pedidos.</p>
          ) : (
            pedidosFiltrados.map(pedido => {
              const estadoInfo = ESTADOS.find(e => e.valor === pedido.estado) || ESTADOS[0];
              const estaExpandido = expandido === pedido.id_pedido;
              const cargandoEste = actualizando === pedido.id_pedido;

              return (
                <div key={pedido._id} className={`pedido-card ${estaExpandido ? 'expandido' : ''}`}>

                  {/* Cabecera clickeable */}
                  <div
                    className="pedido-cabecera"
                    onClick={() => setExpandido(estaExpandido ? null : pedido.id_pedido)}
                  >
                    <div className="pedido-meta">
                      <span className="pedido-id">{pedido.id_pedido}</span>
                      <span className="pedido-fecha">{pedido.fecha}</span>
                      <span className="pedido-cliente">{pedido.cliente?.nombre}</span>
                    </div>
                    <div className="pedido-resumen">
                      <span className="pedido-entrega">
                        {pedido.tipoEntrega === 'retiro' ? '🏪 Retiro' : '🚚 Envío'}
                      </span>
                      <span className="pedido-total">${pedido.total?.toLocaleString('es-AR')}</span>
                      <span
                        className="pedido-badge"
                        style={{
                          background: estadoInfo.color + '22',
                          color: estadoInfo.color,
                          border: `1px solid ${estadoInfo.color}55`,
                        }}
                      >
                        {estadoInfo.etiqueta}
                      </span>
                      <span className="pedido-toggle">{estaExpandido ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {/* Detalle expandible */}
                  {estaExpandido && (
                    <div className="pedido-detalle">
                      <div className="detalle-columnas">

                        {/* Productos */}
                        <div className="detalle-seccion">
                          <h4>Productos</h4>
                          {pedido.productos?.map((prod, i) => (
                            <div key={i} className="detalle-prod-fila">
                              <span className="dp-nombre">{prod.nombre}</span>
                              <span className="dp-cantidad">×{prod.cantidad}</span>
                              <span className="dp-precio">${(prod.precio * prod.cantidad).toLocaleString('es-AR')}</span>
                            </div>
                          ))}
                          <div className="detalle-totales">
                            {pedido.subtotal != null && (
                              <span>Subtotal: ${pedido.subtotal.toLocaleString('es-AR')}</span>
                            )}
                            {pedido.costoEnvio > 0 && (
                              <span>Envío: ${pedido.costoEnvio.toLocaleString('es-AR')}</span>
                            )}
                            <strong>Total: ${pedido.total?.toLocaleString('es-AR')}</strong>
                          </div>
                        </div>

                        {/* Cliente */}
                        <div className="detalle-seccion">
                          <h4>Cliente</h4>
                          <p>{pedido.cliente?.email}</p>
                          <p>{pedido.cliente?.telefono}</p>
                          {pedido.tipoEntrega === 'envio' && (
                            <>
                              <p>{pedido.cliente?.direccion}</p>
                              <p>{pedido.cliente?.ciudad} — CP {pedido.cliente?.codigoPostal}</p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Cambiar estado */}
                      <div className="detalle-estado">
                        <span className="estado-label">Cambiar estado:</span>
                        <div className="estado-botones">
                          {ESTADOS.map(est => (
                            <button
                              key={est.valor}
                              className={`btn-estado ${pedido.estado === est.valor ? 'activo' : ''}`}
                              style={
                                pedido.estado === est.valor
                                  ? { background: est.color, color: '#fff', borderColor: est.color }
                                  : { borderColor: est.color + '66', color: est.color }
                              }
                              onClick={() => cambiarEstado(pedido.id_pedido, est.valor)}
                              disabled={cargandoEste || pedido.estado === est.valor}
                            >
                              {cargandoEste ? '...' : est.etiqueta}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
